import React from 'react';
import { Megaphone } from 'lucide-react';

/**
 * De dónde viene un lead: su PROCEDENCIA (dirección, 27/09/2026): «tiene que
 * haber uno de campaña de [cada tratamiento], y así […] Luego, aparte,
 * contacto, web, como estaba lo demás, las demás vías».
 *
 * Y sus OPCIONES (dirección, 30/09/2026): «las opciones son campaña [de cada
 * tratamiento con campaña], web, Instagram, TikTok, y otras. Y en otras que
 * se pueda poner la que es, si se comenta». Lo que antes era otra vía
 * («Recomendación», «Llamada», una procedencia externa…) sale en «Otras» con
 * su nombre («Otras: Llamada»), y lo que llegaba por correo, en «Web».
 *
 * Quién la decide es el servidor (services/procedencia_lead.py, UNA función:
 * lo puesto a mano; si no, su campaña de Meta; si no, «de dónde viene»); aquí
 * solo se pinta. Las CAMPAÑAS van en ámbar y con un megáfono, para que se
 * vean de un vistazo; las demás, en gris; lo que no se sabe («Sin
 * procedencia»), con borde discontinuo, como «Sin identificar» en la marca.
 *
 * Las opciones para ponerla a mano las manda el servidor (`procedencias` de
 * la lista: las campañas de los tratamientos que tienen campaña en Campañas,
 * y las cuatro de siempre). Estas son solo las que no dependen de ninguna
 * clínica, por si un servidor viejo no las manda.
 */
export const OTRAS = 'otras';
export const TOPE_DETALLE_OTRAS = 60;

export const PROCEDENCIAS_POR_DEFECTO = {
  campanas: [],
  vias: [
    { clave: 'web', nombre: 'Web' },
    { clave: 'instagram', nombre: 'Instagram' },
    { clave: 'tiktok', nombre: 'TikTok' },
    { clave: OTRAS, nombre: 'Otras' },
  ],
};

export const SIN_PROCEDENCIA = 'Sin procedencia';

export const esCampana = (clave) => String(clave || '').startsWith('campana:');

/*
 * VARIAS PROCEDENCIAS A LA VEZ (dirección, 3/11/2026: «marcar las
 * procedencias que quiera, en cualquier combinación, y ver juntos los
 * contactos de todas ellas»). La selección es una lista de claves, las mismas
 * del servidor, y va en la URL separada por comas
 * (?procedencia=campana:rinoplastia,web). Dos GRUPOS, con la clave de grupo
 * que ya entendía el servidor:
 *  · «Campañas» (`campanas`): todas las campañas;
 *  · «Orgánico» (`vias`): todo lo que NO es campaña (Web, Instagram, TikTok,
 *    Otras y «Sin procedencia»).
 * Un grupo entero va como su clave de grupo (así, «Campañas» sigue cogiendo
 * una campaña nueva que aún no tiene botón); si se apaga una de dentro,
 * quedan las demás sueltas; y si se encienden todas las de dentro, vuelve a
 * ser el grupo. El servidor saca los de CUALQUIERA de las claves.
 */
export const GRUPO_CAMPANAS = 'campanas';
export const GRUPO_ORGANICO = 'vias';
export const SIN = 'sin';
const ALIAS_GRUPO = {
  'campañas': GRUPO_CAMPANAS, 'vías': GRUPO_ORGANICO, organico: GRUPO_ORGANICO, 'orgánico': GRUPO_ORGANICO,
};

/** La selección de la URL: «campana:rinoplastia,web» → ['campana:rinoplastia', 'web'].
 *  Sin repetir; «todas» (o nada) es ninguna. */
export function leerProcedencias(texto) {
  const partes = String(texto || '').split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
  if (partes.some((p) => p === 'todas' || p === 'todos')) return [];
  return [...new Set(partes.map((p) => ALIAS_GRUPO[p] || p))];
}

/** Las claves sueltas de cada grupo: las de sus botones y, en Orgánico,
 *  también «Sin procedencia» aunque ahora no tenga botón (no es campaña). */
function sueltasDe(grupos) {
  return {
    [GRUPO_CAMPANAS]: [...new Set(grupos?.campanas || [])],
    [GRUPO_ORGANICO]: [...new Set([...(grupos?.vias || []), SIN])],
  };
}

function grupoDe(clave, sueltas) {
  if (clave === GRUPO_CAMPANAS || clave === GRUPO_ORGANICO) return clave;
  if (sueltas[GRUPO_CAMPANAS].includes(clave)) return GRUPO_CAMPANAS;
  if (sueltas[GRUPO_ORGANICO].includes(clave)) return GRUPO_ORGANICO;
  return esCampana(clave) ? GRUPO_CAMPANAS : GRUPO_ORGANICO;
}

/** Cómo está un grupo: true (entero), 'mixed' (alguna de dentro) o false. */
export function estadoDelGrupo(seleccion, grupo, grupos) {
  const sueltas = sueltasDe(grupos);
  if (seleccion.includes(grupo)) return true;
  const dentro = sueltas[grupo].filter((k) => seleccion.includes(k));
  if (dentro.length && dentro.length === sueltas[grupo].length) return true;
  return seleccion.some((k) => grupoDe(k, sueltas) === grupo) ? 'mixed' : false;
}

/** ¿Está encendida esta procedencia suelta (por sí o por su grupo entero)? */
export function procedenciaEncendida(seleccion, clave, grupos) {
  return seleccion.includes(clave) || seleccion.includes(grupoDe(clave, sueltasDe(grupos)));
}

/**
 * La selección después de pulsar un botón. `clave`: '' («Todas»: quita la
 * selección), un grupo (`campanas` / `vias`: lo enciende entero o, si ya lo
 * estaba, lo apaga) o una procedencia (se enciende o se apaga; si su grupo
 * estaba entero, se quedan las demás). `grupos`: {campanas, vias}, las claves
 * de los botones de cada grupo. Sale en el orden de los botones.
 */
export function alternarProcedencia(seleccion, clave, grupos) {
  if (!clave) return [];
  const sueltas = sueltasDe(grupos);
  const sinElGrupo = (sel, g) => sel.filter((k) => grupoDe(k, sueltas) !== g);
  let nueva;
  if (clave === GRUPO_CAMPANAS || clave === GRUPO_ORGANICO) {
    nueva = estadoDelGrupo(seleccion, clave, grupos) === true
      ? sinElGrupo(seleccion, clave) : [...sinElGrupo(seleccion, clave), clave];
  } else {
    const g = grupoDe(clave, sueltas);
    if (seleccion.includes(g)) {
      nueva = [...sinElGrupo(seleccion, g), ...sueltas[g].filter((k) => k !== clave)];
    } else if (seleccion.includes(clave)) {
      nueva = seleccion.filter((k) => k !== clave);
    } else {
      nueva = [...seleccion, clave];
      if (sueltas[g].includes(clave) && sueltas[g].every((k) => nueva.includes(k))) {
        nueva = [...sinElGrupo(nueva, g), g];
      }
    }
  }
  const orden = [GRUPO_CAMPANAS, ...sueltas[GRUPO_CAMPANAS], GRUPO_ORGANICO, ...sueltas[GRUPO_ORGANICO]];
  const sitio = (k) => (orden.includes(k) ? orden.indexOf(k) : orden.length);
  return [...new Set(nueva)].sort((a, b) => sitio(a) - sitio(b));
}

const ESTILO = {
  campana: 'bg-amber-50 text-amber-900 border-amber-300',
  via: 'bg-slate-50 text-slate-700 border-slate-300',
  sin: 'bg-transparent text-muted-foreground border-dashed border-gray-400',
};

/** El estilo de una procedencia (por su clave): campaña, vía o sin. */
export function estiloProcedencia(clave) {
  if (!clave || clave === 'sin') return ESTILO.sin;
  return esCampana(clave) ? ESTILO.campana : ESTILO.via;
}

/**
 * La etiqueta. `inline-flex` y `no-underline` a propósito, como la marca: el
 * tachado de la fila de un contactado no la atraviesa. Un «Otras: …» largo se
 * corta con «…» (entero en el título y en la ficha).
 */
export default function ProcedenciaLead({ procedencia, testid, title, className = '' }) {
  if (!procedencia) return null;
  const campana = esCampana(procedencia.clave);
  const nombre = procedencia.nombre || SIN_PROCEDENCIA;
  const titulo = title || [procedencia.detalle ? nombre : '', procedencia.explicacion]
    .filter(Boolean).join(' — ') || undefined;
  return (
    <span className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5
      text-xs font-medium no-underline ${estiloProcedencia(procedencia.clave)} ${className}`}
      data-testid={testid} data-procedencia={procedencia.clave || 'sin'}
      title={titulo}>
      {campana ? <Megaphone className="w-3 h-3 shrink-0" aria-hidden="true" /> : null}
      <span className="truncate">{nombre}</span>
    </span>
  );
}

/**
 * El desplegable para ponerla a mano: las campañas y las demás, en dos
 * grupos. `actual` ({clave, nombre}) se añade si ya no está entre las
 * opciones (un tratamiento que ha dejado de tener campaña), para que se vea
 * lo que hay. Con «Otras», quien lo usa pide además cuál (`DetalleOtras`).
 */
export function SelectorProcedencia({
  id, value, onChange, opciones, vacio, testid, disabled, actual, className = 'mt-1 block w-full',
}) {
  const ops = opciones?.campanas && opciones?.vias ? opciones : PROCEDENCIAS_POR_DEFECTO;
  const todas = [...ops.campanas, ...ops.vias];
  const falta = actual?.clave && value === actual.clave && !todas.some((o) => o.clave === actual.clave);
  return (
    <select id={id} value={value || ''} data-testid={testid} disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} h-9 rounded-md border border-input bg-background px-3 text-sm`}>
      <option value="">{vacio}</option>
      {falta ? <option value={actual.clave}>{actual.nombre}</option> : null}
      {ops.campanas.length ? (
        <optgroup label="Campañas">
          {ops.campanas.map((o) => <option key={o.clave} value={o.clave}>{o.nombre}</option>)}
        </optgroup>
      ) : null}
      <optgroup label="Otras vías">
        {ops.vias.map((o) => <option key={o.clave} value={o.clave}>{o.nombre}</option>)}
      </optgroup>
    </select>
  );
}

/**
 * Cuál es, con «Otras» (30/09/2026): «si se comenta». Opcional y corto; lo
 * guarda el servidor con la procedencia y se lee en la etiqueta.
 */
export function DetalleOtras({ id, value, onChange, testid, disabled, onEnter, className = '' }) {
  return (
    <input id={id} type="text" value={value || ''} maxLength={TOPE_DETALLE_OTRAS}
      data-testid={testid} disabled={disabled}
      placeholder="¿Cuál? (si lo ha dicho)"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
      className={`h-9 rounded-md border border-input bg-background px-3 text-sm ${className}`} />
  );
}
